/**
 * ClosingDisclosureContext - React Context for managing CD form state
 */

import * as React from 'react';
import type {
  ClosingDisclosureContextValue,
  ClosingDisclosureData,
} from '../types/closingDisclosureData';
import { DEFAULT_CLOSING_DISCLOSURE_DATA } from '../types/closingDisclosureData';

/**
 * Context for Closing Disclosure form data
 */
export const ClosingDisclosureContext = React.createContext<ClosingDisclosureContextValue | null>(
  null,
);

ClosingDisclosureContext.displayName = 'ClosingDisclosureContext';

/**
 * Props for the ClosingDisclosureProvider
 */
export interface ClosingDisclosureProviderProps {
  /** Initial form data */
  initialData?: Partial<ClosingDisclosureData>;
  /** Whether the form is editable */
  editable?: boolean;
  /** Callback when data changes */
  onChange?: (data: ClosingDisclosureData) => void;
  /** Children components */
  children: React.ReactNode;
}

/**
 * Utility to set a nested value by path (immutably)
 */
function setByPath<T extends Record<string, unknown>>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.');
  if (keys.length === 0) return obj;

  // Create a shallow copy of the root object
  const result: Record<string, unknown> = { ...obj };
  let current: Record<string, unknown> = result;

  // Navigate to the parent of the target key
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (key === undefined) continue;

    const nextValue = current[key];
    if (nextValue && typeof nextValue === 'object' && !Array.isArray(nextValue)) {
      current[key] = { ...nextValue };
    } else {
      if (nextValue !== undefined && nextValue !== null) {
        console.warn(`setByPath: Overwriting non-object value at path segment '${key}'`);
      }
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  // Set the final value
  const lastKey = keys[keys.length - 1];
  if (lastKey !== undefined) {
    current[lastKey] = value;
  }

  return result as T;
}

/**
 * Deep merge two objects
 */
function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const result = { ...target } as Record<string, unknown>;

  for (const key of Object.keys(source)) {
    const sourceValue = (source as Record<string, unknown>)[key];
    const targetValue = (target as Record<string, unknown>)[key];

    if (
      targetValue &&
      sourceValue &&
      typeof targetValue === 'object' &&
      typeof sourceValue === 'object' &&
      !Array.isArray(targetValue) &&
      !Array.isArray(sourceValue)
    ) {
      result[key] = deepMerge(
        targetValue as Record<string, unknown>,
        sourceValue as Record<string, unknown>,
      );
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue;
    }
  }

  return result as T;
}

/**
 * Provider component for ClosingDisclosure context
 */
export function ClosingDisclosureProvider({
  initialData,
  editable = false,
  onChange,
  children,
}: ClosingDisclosureProviderProps) {
  // Merge initial data with defaults
  const mergedInitialData = React.useMemo(
    () =>
      deepMerge(
        DEFAULT_CLOSING_DISCLOSURE_DATA as ClosingDisclosureData,
        (initialData || {}) as Partial<ClosingDisclosureData>,
      ),
    // Only compute on mount - initialData changes won't re-merge
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialData],
  );

  const [formData, setFormData] = React.useState<ClosingDisclosureData>(mergedInitialData);
  const [isLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Track if this is the initial render
  const isInitialMount = React.useRef(true);

  // Notify parent of changes (skip initial mount)
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onChange?.(formData);
  }, [formData, onChange]);

  /**
   * Update a specific field by dot-notation path
   * @example updateField('page1.header.closingInformation.closingDate', '2024-01-15')
   */
  const updateField = React.useCallback((path: string, value: unknown) => {
    setFormData((prev) => {
      const updated = setByPath(prev as unknown as Record<string, unknown>, path, value);
      return updated as unknown as ClosingDisclosureData;
    });
    setError(null);
  }, []);

  /**
   * Update an entire page's data
   */
  const updatePage = React.useCallback(
    <K extends keyof ClosingDisclosureData>(page: K, data: ClosingDisclosureData[K]) => {
      setFormData((prev) => ({
        ...prev,
        [page]: data,
      }));
      setError(null);
    },
    [],
  );

  /**
   * Reset form to initial data
   */
  const resetForm = React.useCallback(() => {
    setFormData(mergedInitialData);
    setError(null);
  }, [mergedInitialData]);

  /**
   * Recalculate derived values
   * This is a placeholder - actual calculations would go here
   */
  const recalculate = React.useCallback(() => {
    // TODO: Implement calculation logic
    // This would update calculated fields like totals, cash to close, etc.
    // For now, this is a no-op
  }, []);

  const contextValue = React.useMemo<ClosingDisclosureContextValue>(
    () => ({
      formData,
      editable,
      isLoading,
      error,
      updateField,
      updatePage,
      resetForm,
      recalculate,
    }),
    [formData, editable, isLoading, error, updateField, updatePage, resetForm, recalculate],
  );

  return (
    <ClosingDisclosureContext.Provider value={contextValue}>
      {children}
    </ClosingDisclosureContext.Provider>
  );
}
